(() => {
  if (window.muyaProductInformationLinksReady) return;
  window.muyaProductInformationLinksReady = true;

  const informationSelector = ':is(details[data-muya-product-information], details[data-muya-product-reviews])';
  const reviewSelector = 'details[data-muya-product-reviews]';
  const descriptionSelector = '[data-muya-product-description]';
  const phrase = /Ürün\s+Özellikleri\s*(?:&|ve)\s*Bakım/g;

  const linkDescriptions = (root = document) => {
    root.querySelectorAll(descriptionSelector).forEach((description) => {
      const section = description.closest('.shopify-section');
      const target = section?.querySelector('details[data-muya-product-information]');
      if (!target?.id) return;

      const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (node.parentElement?.closest('a, button, summary, script, style, textarea, [contenteditable]')) {
            return NodeFilter.FILTER_REJECT;
          }
          phrase.lastIndex = 0;
          return phrase.test(node.textContent) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);

      nodes.forEach((node) => {
        phrase.lastIndex = 0;
        const fragment = document.createDocumentFragment();
        let offset = 0;
        for (const match of node.textContent.matchAll(phrase)) {
          fragment.append(document.createTextNode(node.textContent.slice(offset, match.index)));
          const link = document.createElement('a');
          link.href = `#${target.id}`;
          link.dataset.muyaInformationLink = '';
          link.textContent = match[0];
          fragment.append(link);
          offset = match.index + match[0].length;
        }
        fragment.append(document.createTextNode(node.textContent.slice(offset)));
        node.replaceWith(fragment);
      });
    });
  };

  const summarySelector = `${informationSelector} > summary`;
  const clearPointerFocus = () => {
    document.querySelectorAll(`${summarySelector}[data-muya-pointer-focus]`).forEach((summary) => {
      summary.removeAttribute('data-muya-pointer-focus');
    });
  };
  // AccessiBe emphasizes :focus even for pointer input. Scope the exception to
  // this control and this input mode; keyboard/assistive activation keeps focus.
  document.addEventListener('pointerdown', (event) => {
    clearPointerFocus();
    event.target.closest?.(summarySelector)?.setAttribute('data-muya-pointer-focus', '');
  }, true);
  document.addEventListener('keydown', clearPointerFocus, true);
  document.addEventListener('focusout', (event) => {
    event.target.removeAttribute?.('data-muya-pointer-focus');
  }, true);

  const openInformation = (target, pointer = false) => {
    document.dispatchEvent(new CustomEvent('muya:information-open'));
    const summary = target.querySelector(':scope > summary');
    summary?.toggleAttribute('data-muya-pointer-focus', pointer);
    // The theme setter closes sibling rows and scrolls after their transition.
    // A separate scroll here would race that built-in behaviour.
    target.open = true;
    summary?.focus({ preventScroll: true });
  };

  const resolveHash = (hash) => {
    try {
      const id = decodeURIComponent(hash.slice(1));
      const reviewAlias = /^MuyaProductReviews-(\d+)$/.exec(id);
      const target = document.getElementById(id) || (reviewAlias && [...document.querySelectorAll(reviewSelector)].find((panel) => panel.dataset.muyaProductReviews === reviewAlias[1]));
      return target?.matches(informationSelector) ? target : null;
    } catch {
      return null;
    }
  };
  const targetFromHash = () => resolveHash(window.location.hash);

  const openHash = () => {
    const target = targetFromHash();
    if (target) openInformation(target);
  };

  // Wait for the theme's customized details setter before handling deep links.
  const accordionReady = customElements.whenDefined('accordion-details');
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    if (link.origin !== location.origin || link.pathname !== location.pathname || link.search !== location.search) return;
    let target;
    try {
      target = resolveHash(link.hash);
    } catch {
      return;
    }
    if (!target?.matches(informationSelector)) return;
    event.preventDefault();
    accordionReady.then(() => {
      if (!target.isConnected) return;
      openInformation(target, event.detail > 0);
      // Preserve the previous location for Back; pushState does not fire hashchange.
      if (location.hash !== link.hash) history.pushState(history.state, '', link.hash);
    });
  });

  const init = () => {
    linkDescriptions();
    accordionReady.then(openHash);
  };

  // On reload the browser can restore scroll after the initial accordion scroll.
  // Reconcile the initial deep link once after pageshow; never override new input
  // or the user's restored position when returning from the back-forward cache.
  const initialHash = location.hash;
  if (initialHash) {
    const initialNavigation = new AbortController();
    const { signal } = initialNavigation;
    let scroller;
    let pendingScroll;
    const cancel = () => {
      // Cancel only the timer created by this reconciliation, never a newer
      // accordion action. The theme waits 250ms before starting its scroll.
      if (pendingScroll && scroller?.scrollTimer === pendingScroll) clearTimeout(pendingScroll);
      initialNavigation.abort();
    };
    ['pointerdown', 'mousedown', 'touchstart', 'wheel', 'keydown', 'click', 'hashchange'].forEach((name) => {
      window.addEventListener(name, cancel, { capture: true, passive: true, signal });
    });
    const reconcile = (event) => {
      if (event?.persisted) { cancel(); return; }
      accordionReady.then(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        const target = targetFromHash();
        const active = document.activeElement;
        // Startup focus can fall back to the document (for example while a tab
        // loads in the background). That is not a new navigation intent. Keep
        // respecting another focused control and all captured user input.
        const focusUnclaimed = !active || active === document.body || active === document.documentElement;
        if (!signal.aborted && location.hash === initialHash && target?.open &&
          (focusUnclaimed || active === target.querySelector(':scope > summary'))) {
          openInformation(target);
          scroller = target.closest('accordions-details');
          pendingScroll = scroller?.scrollTimer;
          setTimeout(() => initialNavigation.abort(), 300);
        } else cancel();
      })));
    };
    if (document.readyState === 'complete') reconcile();
    else window.addEventListener('pageshow', reconcile, { once: true, signal });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => {
    linkDescriptions(event.target);
    const target = targetFromHash();
    if (target && event.target.contains(target)) {
      accordionReady.then(() => {
        if (target.isConnected) openInformation(target);
      });
    }
  });
  window.addEventListener('hashchange', () => accordionReady.then(openHash));
})();
